/**
 * The configuration boundary.
 *
 * Two rules, and the second is the one that matters:
 *
 *  1. Everything here is **public**. `import.meta.env` is compiled into the
 *     bundle every visitor downloads, so a value that reaches this file has
 *     been published. The Supabase URL and anon key are safe there — Row Level
 *     Security decides what they reach. Nothing else is.
 *
 *  2. A server secret must never carry a `VITE_` prefix. The service role key,
 *     the master encryption key, the cron secret and the mail provider's key
 *     are read by server code from `process.env` and are not importable from
 *     here. `scripts/tenant-neutrality.mjs` fails the build if a `.env` file
 *     puts one behind `VITE_`.
 *
 * Missing configuration fails loudly and in plain language. The reference
 * implementation's equivalent returned a null client and let the app look as
 * though it had saved data when there was no database behind it.
 */

export interface PublicConfig {
    supabaseUrl: string;
    supabaseAnonKey: string;
    /** Where invite and password-reset links come back to. */
    appUrl: string;
    /** The public site, for links out of the product. */
    marketingUrl: string;
    mode: string;
}

export interface ConfigProblem {
    name: string;
    detail: string;
}

const REQUIRED = ['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY'] as const;

const LOOPBACK = /^http:\/\/(127\.0\.0\.1|localhost|\[::1\])(:\d+)?(\/|$)/;

const read = (name: string): string =>
    String((import.meta.env as Record<string, unknown>)[name] ?? '').trim();

/** Everything wrong with the configuration, so it can all be fixed in one go. */
export function configProblems(): ConfigProblem[] {
    const problems: ConfigProblem[] = [];

    for (const name of REQUIRED) {
        if (!read(name)) problems.push({ name, detail: 'is not set' });
    }

    // https everywhere, except this machine's own stack: the local Supabase
    // serves plain http on the loopback address, and refusing it made the app
    // impossible to open in a browser against it. Nothing off the machine
    // qualifies, so a deployment cannot be configured into plain http.
    const url = read('VITE_SUPABASE_URL');
    if (url && !/^https:\/\//.test(url) && !LOOPBACK.test(url)) {
        problems.push({ name: 'VITE_SUPABASE_URL', detail: 'must be an https:// URL' });
    }

    for (const name of ['VITE_APP_URL', 'VITE_MARKETING_URL']) {
        const value = read(name);
        if (value && !/^https?:\/\//.test(value)) {
            problems.push({ name, detail: 'must be a full URL, including the scheme' });
        }
    }

    return problems;
}

/**
 * The configuration, or a thrown error naming exactly what is missing.
 *
 * Call `configProblems()` first where you want to show a screen instead of
 * failing — which is what the app does, so a misconfigured deployment says so
 * rather than rendering a broken shell.
 */
export function publicConfig(): PublicConfig {
    const problems = configProblems();
    if (problems.length) {
        throw new Error(
            `Configuration is incomplete: ${problems.map(p => `${p.name} ${p.detail}`).join('; ')}.`,
        );
    }
    return {
        supabaseUrl: read('VITE_SUPABASE_URL'),
        supabaseAnonKey: read('VITE_SUPABASE_ANON_KEY'),
        appUrl: read('VITE_APP_URL') || window.location.origin,
        marketingUrl: read('VITE_MARKETING_URL') || 'https://timelypayment.in',
        mode: import.meta.env.MODE,
    };
}
